import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AclList } from "../types/acl";
import { Region } from "../types/regions";
import { Role } from "../types/roles";
import { UserClaims, UserType } from "../types/users";

export type UsersSlice = {
  aclList: AclList;
  availableRegions: Region[];
  availableRoles: Role[];
  gettingAclList: boolean;
  getAclListSuccess: boolean;
  getAclListFailed: boolean;
  getAclListFailedMessage: string;
  isAuthenticated: boolean;
  display2FALoginField: boolean;
  loginFail: boolean;
  loggingIn: boolean;
  loginFailMessage: string;
  token: boolean; //unused
  userTypes: UserType[];
  userClaims: Record<string, never> | UserClaims;
  isAuthenticating: boolean;
  submitSucceeded: boolean;
  submittingForm: boolean;
  openResetPasswordModal: boolean;
  submittingResetPassword: boolean;
  resetPasswordFailed: boolean;
  resetPasswordMessage: string;
  openDeleteAccountModal: boolean;
  deleteAccountFailed: boolean;
  deleteAccountMessage: string;
};

const initialState: UsersSlice = {
  aclList: {},
  availableRegions: [],
  availableRoles: [],
  gettingAclList: false,
  getAclListSuccess: false,
  getAclListFailed: false,
  getAclListFailedMessage: "",
  isAuthenticated: false,
  display2FALoginField: false,
  loginFail: false,
  loggingIn: false,
  loginFailMessage: "",
  token: false, //unused
  userTypes: [],
  userClaims: {},
  isAuthenticating: false,
  submitSucceeded: false,
  submittingForm: false,
  openResetPasswordModal: false,
  submittingResetPassword: false,
  resetPasswordFailed: false,
  resetPasswordMessage: "",
  openDeleteAccountModal: false,
  deleteAccountFailed: false,
  deleteAccountMessage: ""
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    loginSuccess(state) {
      state.loginFail = false;
      state.loggingIn = false;
    },
    loginStart(state) {
      state.loginFail = false;
      // state.loginSuccess = false; This was in reducer but there's no corresponding field
      state.loggingIn = true;
    },
    setToken() {
      //TODO: check this
      // state.token = action.payload;
    },
    loginFail(state, action: PayloadAction<string>) {
      state.loginFail = true;
      state.loggingIn = false;
      state.loginFailMessage = action.payload;
    },
    logoutUser(state) {
      // state.token = false;
      state.userClaims = {};
      state.isAuthenticated = false;
    },
    startAuth(state) {
      state.isAuthenticating = true;
    },
    authFail(state) {
      state.isAuthenticating = false;
      state.isAuthenticated = false;
      state.userClaims = {};
      // state.token = false; //check
    },
    authSuccess(state) {
      state.isAuthenticated = true;
      state.isAuthenticating = false;
    },
    setUserClaims(state, action: PayloadAction<UserClaims>) {
      state.userClaims = action.payload;
    },
    setUserTypes(state, action: PayloadAction<UserType[]>) {
      state.userTypes = action.payload;
    },
    startSubmittingForm(state) {
      state.submittingForm = true;
    },
    //Note: action unused
    setResetPasswordModal(state, action: PayloadAction<{ isOpen: boolean }>) {
      state.openResetPasswordModal = action.payload.isOpen;
    },
    //Note: action unused
    resetPasswordStart(state) {
      state.submittingResetPassword = true;
      state.resetPasswordFailed = false;
      state.resetPasswordMessage = "";
    },
    resetPasswordFailed(state, action: PayloadAction<string>) {
      state.submittingResetPassword = false;
      state.resetPasswordFailed = true;
      state.resetPasswordMessage = action.payload;
    },
    resetResetPasswordModal(state) {
      state.openResetPasswordModal = false;
      state.submittingResetPassword = false;
      state.resetPasswordFailed = false;
      state.resetPasswordMessage = "";
    },
    setDeleteAccountModal(state, action: PayloadAction<boolean>) {
      state.openDeleteAccountModal = action.payload;
    },
    //Note: action unused
    deleteAccountFailed(state, action: PayloadAction<string>) {
      state.submittingForm = false;
      state.submitSucceeded = false;
      state.deleteAccountFailed = true;
      state.deleteAccountMessage = action.payload;
    },
    //Note: action unused
    deleteAccountSuccess(state) {
      state.submittingForm = false;
      state.submitSucceeded = true;
      state.deleteAccountFailed = false;
      state.deleteAccountMessage = "";
    },
    sendGetAclList(state) {
      state.aclList = {};
      state.gettingAclList = true;
      state.getAclListSuccess = false;
      state.getAclListFailed = false;
      state.getAclListFailedMessage = "";
    },
    getAclListSuccess(state, action: PayloadAction<AclList>) {
      state.aclList = action.payload;
      state.gettingAclList = false;
      state.getAclListSuccess = true;
      state.getAclListFailed = false;
      state.getAclListFailedMessage = "";
    },
    getAclListFail(state, action: PayloadAction<string>) {
      state.aclList = {};
      state.gettingAclList = false;
      state.getAclListSuccess = false;
      state.getAclListFailed = true;
      state.getAclListFailedMessage = action.payload;
    },
    setAvailableRegions(state, action: PayloadAction<Region[]>) {
      state.availableRegions = action.payload;
    },
    setAvailableRoles(state, action: PayloadAction<Role[]>) {
      state.availableRoles = action.payload;
    },
    show2FALoginField(state) {
      state.loginFail = false;
      state.loggingIn = false;
      state.display2FALoginField = true;
    },
    hide2FALoginField(state) {
      state.loginFail = false;
      state.loggingIn = false;
      state.display2FALoginField = false;
    }
  }
});

export const {
  authFail,
  authSuccess,
  getAclListFail,
  getAclListSuccess,
  loginFail,
  loginStart,
  loginSuccess,
  logoutUser,
  resetPasswordFailed,
  resetResetPasswordModal,
  sendGetAclList,
  setAvailableRegions,
  setAvailableRoles,
  setDeleteAccountModal,
  setToken,
  setUserClaims,
  setUserTypes,
  startAuth,
  startSubmittingForm,
  show2FALoginField,
  hide2FALoginField
} = usersSlice.actions;

export default usersSlice.reducer;
